import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericButton from "@ui-components/Button/GenericButton";
import {
  formatContractDate,
  withFallback,
} from "kontrolEdge/helpers/formattersShappedData";
import { ResourceTagsTableCell } from "../Shared/ResourceTagsTableCell";
import type { ResourceTagSummary } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";
import { mapKeListResourceTags } from "../Shared/mapKeListResourceTags";
import {
  shapeCarrierAccess,
  formatCarrierAccessNames,
  type CarrierAccess,
} from "kontrolEdge/helpers/carrierAccess";

export interface ExtenderPop {
  id: number;
  /** Service ID (80-XXXXXX / 83-XXXXXX). */
  serviceId: string;
  name: string;
  /** Rich tags from the Tags Manager pivot (§5.5). */
  tagsRich?: ResourceTagSummary[];
  /** Lifecycle: draft | provisioning | active | suspended | decommissioned. */
  status: string;
  serviceSubtype: string;
  /**
   * Carriers reaching THIS Extender PoP's router; an Extender PoP is always
   * reached by carrier access (one or more), never by a cross connect.
   */
  carrierAccess: CarrierAccess[];
  /** Customer site, derived from the router's inventory location. */
  site: {
    name: string;
    datacenterId?: string;
  } | null;
  /** The router this Extender PoP is. Null while the service is a draft. */
  router: {
    id: number;
    hostname: string | null;
  } | null;
  /** HA partner at the same site ("entre ellos se conectan"). */
  haPeer: {
    id: number;
    serviceId: string;
  } | null;
  term: string;
  renewDate: string;
  renewDateRaw: Date | null;
  customer?: {
    name: string;
    chargebeeCustomerId: string;
  };
}

/**
 * Homologated with the Ports "Physical Connect" cell, but this service is
 * always reached by carrier access — the type line is fixed and only the
 * carrier names vary (one or more, e.g. "IENTC, MCM").
 */
const CarrierAccessCell: React.FC<{ row: ExtenderPop }> = ({ row }) => {
  const names = formatCarrierAccessNames(row.carrierAccess);
  if (!names) return <div>-</div>;

  return (
    <div className="flex flex-col leading-tight">
      <span className="text-xs text-semantic-text-secondary">
        Type: Carrier Access
      </span>
      <span className="whitespace-nowrap">
        <span className="text-xs text-semantic-text-secondary">Carrier: </span>
        {names}
      </span>
    </div>
  );
};

interface StatusPresentation {
  variant: "success" | "default" | "error" | "warning";
  label: string;
  rank: number;
}

/** Single source for the Status pill and its sort order. */
const STATUS_PRESENTATION: Record<string, StatusPresentation> = {
  active: { variant: "success", label: "Active", rank: 5 },
  provisioning: { variant: "warning", label: "Provisioning", rank: 4 },
  draft: { variant: "default", label: "Draft", rank: 3 },
  suspended: { variant: "warning", label: "Suspended", rank: 2 },
  decommissioned: { variant: "error", label: "Decommissioned", rank: 1 },
};

export const extenderPopStatusPresentation = (
  status?: string | null
): StatusPresentation =>
  STATUS_PRESENTATION[status ?? ""] ?? {
    variant: "default",
    label: "Unknown",
    rank: 0,
  };

export const extenderPopColumns: GenericTableColumn<ExtenderPop>[] = [
  {
    header: "Extended PoP ID",
    accessor: "serviceId",
    renderCell: (row) => <GenericButton variant="brand" label={row.serviceId} />,
  },
  {
    header: "Name",
    accessor: "name",
  },
  {
    header: "Status",
    renderCell: (row) => {
      const { variant, label } = extenderPopStatusPresentation(row.status);
      return <GenericTag variant={variant} label={label} size="small" />;
    },
    sortAccessor: (row) => extenderPopStatusPresentation(row.status).rank,
  },
  {
    header: "Tags",
    renderCell: (row) => (
      <ResourceTagsTableCell
        tagsRich={row.tagsRich}
        maxVisible={1}
        triggerSingleRow
      />
    ),
  },
  {
    header: "Site",
    renderCell: (row) => <div>{row.site?.name ?? "-"}</div>,
    sortAccessor: (row) => row.site?.name ?? "",
  },
  {
    header: "Carrier Access",
    renderCell: (row) => <CarrierAccessCell row={row} />,
    sortAccessor: (row) => formatCarrierAccessNames(row.carrierAccess),
  },
  {
    header: "HA Peer",
    renderCell: (row) =>
      row.haPeer ? (
        <span className="text-semantic-text-brand">{row.haPeer.serviceId}</span>
      ) : (
        <div>-</div>
      ),
    sortAccessor: (row) => row.haPeer?.serviceId ?? "",
  },
  {
    header: "Term",
    renderCell: (row) => <div>{row.term}</div>,
  },
  {
    header: "Renew Date",
    accessor: "renewDate",
  },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const shapeExtenderPopData = (rows: any[]): ExtenderPop[] => {
  return rows.map((r) => {
    const customer = r.customer
      ? {
          name: r.customer.name ?? "-",
          chargebeeCustomerId: r.customer.chargebeeCustomerId ?? "-",
        }
      : undefined;

    const subscription = r.subscription ?? null;
    const renewIso =
      subscription?.renewDate && subscription.renewDate !== "N/A"
        ? subscription.renewDate
        : null;
    const rawDate = renewIso ? new Date(renewIso) : null;
    const hasTerm = Boolean(subscription?.term);

    return {
      id: r.id,
      serviceId: withFallback(r.serviceId),
      name: withFallback(r.name),
      tagsRich: mapKeListResourceTags(r),
      status: r.status ?? "unknown",
      serviceSubtype: withFallback(r.serviceSubtype),
      carrierAccess: shapeCarrierAccess(r),
      site: r.site
        ? {
            name: r.site.name ?? "-",
            datacenterId: r.site.datacenterId ?? undefined,
          }
        : null,
      router: r.router
        ? {
            id: r.router.id,
            hostname: r.router.hostname ?? null,
          }
        : null,
      haPeer: r.haPeer
        ? {
            id: r.haPeer.id,
            serviceId: withFallback(r.haPeer.serviceId),
          }
        : null,
      term: hasTerm
        ? `${subscription.term} ${withFallback(subscription.term_unit)}`
        : "-",
      renewDate: renewIso ? formatContractDate(renewIso) : "-",
      renewDateRaw: rawDate && !isNaN(rawDate.getTime()) ? rawDate : null,
      customer,
    };
  });
};
